import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {defineMessages, useIntl} from 'react-intl';

import Modal from '../components/modal/modal.jsx';
import {
    getCloudProjectKey,
    hasLoginToken,
    listCloudProjects,
    saveCloudProject,
    setCloudProjectKey
} from '../lib/domi-cloud-project';
import {setProjectUnchanged} from '../reducers/project-changed';
import {setProjectTitle} from '../reducers/project-title';
import {closeLoadingProject, openLoadingProject} from '../reducers/modals';

import styles from './domi-cloud-project.css';

const AUTO_SAVE_INTERVAL_MS = 5 * 60 * 1000;
const AUTO_SAVE_RETRY_DELAY_MS = 60 * 1000;
const AUTO_SAVE_NOTICE_DURATION_MS = 4000;

const messages = defineMessages({
    title: {
        id: 'gui.domiCloud.title',
        defaultMessage: 'My cloud projects',
        description: 'Title of the Domi cloud projects modal'
    },
    projectsEmpty: {
        id: 'gui.domiCloud.projectsEmpty',
        defaultMessage: 'No cloud projects yet. Save the current project to create one.',
        description: 'Message shown when there are no Domi cloud projects'
    },
    loading: {
        id: 'gui.domiCloud.loading',
        defaultMessage: 'Loading cloud projects...',
        description: 'Message shown while cloud projects load'
    },
    saved: {
        id: 'gui.domiCloud.saved',
        defaultMessage: 'Project saved to cloud.',
        description: 'Message shown after a cloud project is saved'
    },
    autoSaving: {
        id: 'gui.domiCloud.autoSaving',
        defaultMessage: 'Auto-saving...',
        description: 'Status shown while a Domi cloud project is auto-saving'
    },
    autoSaved: {
        id: 'gui.domiCloud.autoSaved',
        defaultMessage: 'Auto-saved',
        description: 'Status shown after a Domi cloud project is auto-saved'
    },
    autoSaveFailed: {
        id: 'gui.domiCloud.autoSaveFailed',
        defaultMessage: 'Auto-save failed. Your changes are still in this workspace.',
        description: 'Status shown after Domi cloud project auto-save fails'
    },
    untitledProject: {
        id: 'gui.domiCloud.untitledProject',
        defaultMessage: 'Domi Scratch Project',
        description: 'Fallback name for an unnamed cloud project'
    },
    open: {
        id: 'gui.domiCloud.open',
        defaultMessage: 'Open',
        description: 'Button for opening a cloud project'
    },
    opening: {
        id: 'gui.domiCloud.opening',
        defaultMessage: 'Opening...',
        description: 'Button label while a cloud project opens'
    },
    replaceWarning: {
        id: 'gui.domiCloud.replaceWarning',
        defaultMessage: 'Opening this project will replace the current workspace. Continue?',
        description: 'Confirmation shown before loading a cloud project'
    }
});

const ProjectItem = ({onOpen, openingId, project}) => {
    const intl = useIntl();
    const handleOpen = useCallback(() => onOpen(project), [onOpen, project]);
    return (
        <div className={styles.item}>
            <div>
                <div className={styles.projectName}>
                    {project.projectName || intl.formatMessage(messages.untitledProject)}
                </div>
                <div className={styles.projectMeta}>
                    {new Date(project.updateTime).toLocaleString()}
                </div>
            </div>
            <button
                className={styles.openButton}
                disabled={openingId !== null}
                onClick={handleOpen}
            >
                {intl.formatMessage(openingId === project.id ? messages.opening : messages.open)}
            </button>
        </div>
    );
};

ProjectItem.propTypes = {
    onOpen: PropTypes.func.isRequired,
    openingId: PropTypes.number,
    project: PropTypes.shape({
        fileUrl: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
        projectKey: PropTypes.string.isRequired,
        projectName: PropTypes.string,
        updateTime: PropTypes.string
    }).isRequired
};

const DomiCloudProject = ({
    autoSaveIntervalMs = AUTO_SAVE_INTERVAL_MS,
    autoSaveRetryDelayMs = AUTO_SAVE_RETRY_DELAY_MS,
    children,
    isRtl,
    onCreateNew,
    onLoadingFinished,
    onLoadingStarted,
    onProjectLoaded,
    onSetProjectTitle,
    projectChanged,
    projectTitle,
    saveProjectSb3,
    vm
}) => {
    const intl = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [openingId, setOpeningId] = useState(null);
    const [projects, setProjects] = useState([]);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [autoSaveMode, setAutoSaveMode] = useState('normal');
    const [autoSaveNotice, setAutoSaveNotice] = useState(null);
    const activeSaveRef = useRef(null);
    const autoSaveActionRef = useRef(null);
    const changeSequenceRef = useRef(0);
    const projectChangedRef = useRef(projectChanged);
    const handleClose = useCallback(() => setIsOpen(false), []);

    useEffect(() => {
        projectChangedRef.current = projectChanged;
        if (!projectChanged) setAutoSaveMode('normal');
    }, [projectChanged]);

    useEffect(() => {
        const handleProjectChanged = () => {
            changeSequenceRef.current += 1;
            projectChangedRef.current = true;
            setAutoSaveMode(currentMode => {
                if (currentMode === 'paused') return 'normal';
                return currentMode;
            });
        };
        vm.on('PROJECT_CHANGED', handleProjectChanged);
        return () => vm.removeListener('PROJECT_CHANGED', handleProjectChanged);
    }, [vm]);

    useEffect(() => {
        if (!autoSaveNotice || autoSaveNotice === 'saving') return;
        const timeoutId = setTimeout(() => setAutoSaveNotice(null), AUTO_SAVE_NOTICE_DURATION_MS);
        return () => clearTimeout(timeoutId);
    }, [autoSaveNotice]);

    useEffect(() => {
        const handleOnline = () => {
            if (projectChangedRef.current) setAutoSaveMode('normal');
        };
        window.addEventListener('online', handleOnline);
        return () => window.removeEventListener('online', handleOnline);
    }, []);

    const loadProjects = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            setProjects(await listCloudProjects());
        } catch (requestError) {
            setError(requestError.message || String(requestError));
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleOpenProjects = useCallback(() => {
        setMessage('');
        setIsOpen(true);
        loadProjects();
    }, [loadProjects]);

    const saveCurrentProject = useCallback(({
        autoSaveAttempt = 'normal',
        automatic = false,
        showProjects = true
    } = {}) => {
        if (activeSaveRef.current) return activeSaveRef.current;

        const sequenceAtSaveStart = changeSequenceRef.current;
        setIsSaving(true);
        setError('');
        setMessage('');
        if (automatic) setAutoSaveNotice('saving');
        else setAutoSaveNotice(null);

        const savePromise = (async () => {
            try {
                const content = await saveProjectSb3();
                await saveCloudProject(content, projectTitle);

                if (changeSequenceRef.current === sequenceAtSaveStart) {
                    projectChangedRef.current = false;
                    onProjectLoaded();
                }

                setAutoSaveMode('normal');
                if (automatic) setAutoSaveNotice('saved');
                if (showProjects) {
                    setMessage(intl.formatMessage(messages.saved));
                    setIsOpen(true);
                    await loadProjects();
                }
                return true;
            } catch (saveError) {
                if (automatic) {
                    setAutoSaveNotice('failed');
                    setAutoSaveMode(autoSaveAttempt === 'normal' ? 'retry' : 'paused');
                } else {
                    setError(saveError.message || String(saveError));
                    setIsOpen(true);
                }
                return false;
            } finally {
                activeSaveRef.current = null;
                setIsSaving(false);
            }
        })();

        activeSaveRef.current = savePromise;
        return savePromise;
    }, [intl, loadProjects, onProjectLoaded, projectTitle, saveProjectSb3]);

    const handleSave = useCallback(async () => {
        if (activeSaveRef.current) await activeSaveRef.current;
        if (projectChangedRef.current || !getCloudProjectKey()) {
            return saveCurrentProject();
        }
        setMessage(intl.formatMessage(messages.saved));
        setError('');
        setIsOpen(true);
        await loadProjects();
        return true;
    }, [intl, loadProjects, saveCurrentProject]);

    const handleSaveBeforeNew = useCallback(async () => {
        if (activeSaveRef.current) await activeSaveRef.current;
        let shouldSave = projectChangedRef.current || !getCloudProjectKey();
        while (shouldSave) {
            const saved = await saveCurrentProject({showProjects: false});
            if (!saved) return false;
            shouldSave = projectChangedRef.current;
        }
        return true;
    }, [saveCurrentProject]);

    const handleAutoSave = useCallback(autoSaveAttempt => {
        if (!projectChangedRef.current || activeSaveRef.current) return;
        if (!hasLoginToken()) {
            setAutoSaveMode('paused');
            return;
        }
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            setAutoSaveMode(autoSaveAttempt === 'normal' ? 'retry' : 'paused');
            return;
        }
        saveCurrentProject({
            autoSaveAttempt,
            automatic: true,
            showProjects: false
        });
    }, [saveCurrentProject]);

    useEffect(() => {
        autoSaveActionRef.current = handleAutoSave;
    }, [handleAutoSave]);

    useEffect(() => {
        if (!projectChanged || isSaving || autoSaveMode === 'paused') return;
        const delay = autoSaveMode === 'retry' ? autoSaveRetryDelayMs : autoSaveIntervalMs;
        const timeoutId = setTimeout(() => {
            if (autoSaveActionRef.current) autoSaveActionRef.current(autoSaveMode);
        }, delay);
        return () => clearTimeout(timeoutId);
    }, [autoSaveIntervalMs, autoSaveMode, autoSaveRetryDelayMs, isSaving, projectChanged]);

    const handleCreateNew = useCallback(
        () => onCreateNew(handleSaveBeforeNew),
        [handleSaveBeforeNew, onCreateNew]
    );

    const handleOpenProject = useCallback(async project => {
        const shouldReplace = !projectChanged ||
            window.confirm(intl.formatMessage(messages.replaceWarning)); // eslint-disable-line no-alert
        if (!shouldReplace) return;

        setOpeningId(project.id);
        setError('');
        onLoadingStarted();
        try {
            const separator = project.fileUrl.includes('?') ? '&' : '?';
            const cacheKey = Date.parse(project.updateTime) || Date.now();
            const response = await fetch(`${project.fileUrl}${separator}t=${cacheKey}`);
            if (!response.ok) throw new Error(`项目文件加载失败 (${response.status})`);
            await vm.loadProject(await response.arrayBuffer());
            setCloudProjectKey(project.projectKey);
            onSetProjectTitle(project.projectName || intl.formatMessage(messages.untitledProject));
            projectChangedRef.current = false;
            setAutoSaveMode('normal');
            onProjectLoaded();
            setIsOpen(false);
        } catch (loadError) {
            setError(loadError.message || String(loadError));
        } finally {
            onLoadingFinished();
            setOpeningId(null);
        }
    }, [intl, onLoadingFinished, onLoadingStarted, onProjectLoaded, onSetProjectTitle, projectChanged, vm]);

    return (
        <React.Fragment>
            {children({
                isSaving,
                onCreateNew: handleCreateNew,
                onOpenProjects: handleOpenProjects,
                onSave: handleSave
            })}
            {autoSaveNotice && (
                <div
                    className={autoSaveNotice === 'failed' ?
                        `${styles.autoSaveStatus} ${styles.autoSaveStatusError}` : styles.autoSaveStatus}
                    role="status"
                >
                    {intl.formatMessage(messages[
                        autoSaveNotice === 'saving' ? 'autoSaving' :
                            autoSaveNotice === 'saved' ? 'autoSaved' : 'autoSaveFailed'
                    ])}
                </div>
            )}
            {isOpen && (
                <Modal
                    className={styles.modal}
                    contentLabel={intl.formatMessage(messages.title)}
                    isRtl={isRtl}
                    onRequestClose={handleClose}
                >
                    <div className={styles.body}>
                        {message && <div className={styles.success}>{message}</div>}
                        {error && <div className={styles.error}>{error}</div>}
                        {isLoading ? (
                            <div className={styles.empty}>{intl.formatMessage(messages.loading)}</div>
                        ) : projects.length === 0 ? (
                            <div className={styles.empty}>{intl.formatMessage(messages.projectsEmpty)}</div>
                        ) : (
                            <div className={styles.list}>
                                {projects.map(project => (
                                    <ProjectItem
                                        key={project.projectKey}
                                        openingId={openingId}
                                        project={project}
                                        onOpen={handleOpenProject}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </React.Fragment>
    );
};

DomiCloudProject.propTypes = {
    autoSaveIntervalMs: PropTypes.number,
    autoSaveRetryDelayMs: PropTypes.number,
    children: PropTypes.func.isRequired,
    isRtl: PropTypes.bool,
    onCreateNew: PropTypes.func.isRequired,
    onLoadingFinished: PropTypes.func.isRequired,
    onLoadingStarted: PropTypes.func.isRequired,
    onProjectLoaded: PropTypes.func.isRequired,
    onSetProjectTitle: PropTypes.func.isRequired,
    projectChanged: PropTypes.bool,
    projectTitle: PropTypes.string,
    saveProjectSb3: PropTypes.func.isRequired,
    vm: PropTypes.shape({
        loadProject: PropTypes.func.isRequired,
        on: PropTypes.func.isRequired,
        removeListener: PropTypes.func.isRequired
    }).isRequired
};

const mapStateToProps = state => ({
    isRtl: state.locales.isRtl,
    projectChanged: state.scratchGui.projectChanged,
    projectTitle: state.scratchGui.projectTitle,
    saveProjectSb3: state.scratchGui.vm.saveProjectSb3.bind(state.scratchGui.vm),
    vm: state.scratchGui.vm
});

const mapDispatchToProps = dispatch => ({
    onLoadingFinished: () => dispatch(closeLoadingProject()),
    onLoadingStarted: () => dispatch(openLoadingProject()),
    onProjectLoaded: () => dispatch(setProjectUnchanged()),
    onSetProjectTitle: title => dispatch(setProjectTitle(title))
});

export {DomiCloudProject};

export default connect(mapStateToProps, mapDispatchToProps)(DomiCloudProject);
